<template>
  <el-dialog
    title="分享小票"
    :visible.sync="visible"
    width="400px"
    :close-on-click-modal="false"
  >
    <div v-if="!order" style="text-align:center;padding:40px;color:#999;">
      暂无订单数据
    </div>
    
    <div v-else>
      <!-- 小票预览 -->
      <div id="receipt-preview" class="receipt-preview">
        <div class="receipt-content">
          <!-- 商户信息 -->
          <div class="receipt-header">
            <h2>{{ merchantName }}</h2>
            <p v-if="merchantAddress">{{ merchantAddress }}</p>
            <p v-if="merchantPhone">Tel: {{ merchantPhone }}</p>
            <p v-if="gstNo">GST No: {{ gstNo }}</p>
          </div>
          
          <div class="receipt-divider">********************************</div>
          
          <!-- 交易信息 -->
          <div class="receipt-info">
            <p>Terminal: {{ order.sourceChannel || 'WEB' }}</p>
            <p>{{ orderTime }}</p>
            <p>Invoice No: {{ order.invoiceNo || order.orderNo || 'N/A' }}</p>
            <p>Order: {{ order.orderNo }}</p>
            <p>Cashier: {{ order.createdBy || '001' }}</p>
          </div>
          
          <div class="receipt-divider">--------------------------------</div>
          
          <!-- 商品明细 -->
          <div class="receipt-items">
            <p class="item-header">No   Item            Qty    Amt</p>
            <div class="receipt-divider">--------------------------------</div>
            <div v-for="(item, idx) in order.items" :key="idx" class="item-row">
              <p class="item-name">{{ String(idx + 1).padStart(2, '0') }} {{ truncate(item.productName || item.name, 12) }} {{ String(item.quantity).padStart(5) }} {{ String(item.subtotal).padStart(7) }}</p>
              <p v-if="item.taxRate > 0" class="item-detail">
                GST {{ Math.round(item.taxRate * 100) }}%: {{ formatMoney(item.taxAmount) }}
              </p>
              <p v-else-if="item.taxRate === 0" class="item-detail">(Tax Free)</p>
            </div>
            <div class="receipt-divider">--------------------------------</div>
          </div>
          
          <!-- 金额汇总 -->
          <div class="receipt-summary">
            <p>Subtotal:    SGD{{ formatMoney(order.subtotal) }}</p>
            <p v-if="showGst">GST ({{ gstRate }}%):   SGD{{ formatMoney(gstAmount) }}</p>
            <p v-else-if="gstAmount > 0">GST:         SGD{{ formatMoney(gstAmount) }}</p>
            <p v-else>Tax Free</p>
            <div class="receipt-divider">--------------------------------</div>
            <p class="total">Total:       SGD{{ formatMoney(order.totalAmount) }}</p>
            <p>Items: {{ (order.items || []).length }}</p>
            <p>Pay: {{ order.paymentMethod || 'COD' }}</p>
            
            <!-- 银行信息 -->
            <template v-if="showBankInfo">
              <div class="receipt-divider">--------------------------------</div>
              <p>Bank: {{ bankName }}</p>
              <p v-if="bankAccountName">Acc:  {{ bankAccountName }}</p>
              <p v-if="bankAccountNumber">No:   {{ bankAccountNumber }}</p>
            </template>
            
            <div class="receipt-divider">--------------------------------</div>
            
            <!-- 条形码 -->
            <p class="barcode">{{ order.orderNo }}</p>
            
            <!-- 备注 -->
            <template v-if="order.remark">
              <div class="receipt-divider">--------------------------------</div>
              <p><b>Note</b></p>
              <p>{{ truncate(order.remark, 30) }}</p>
            </template>
            
            <div class="receipt-divider">--------------------------------</div>
            <p class="footer">Thank You / 谢谢惠顾</p>
          </div>
          
          <div class="receipt-divider">================================</div>
        </div>
      </div>
      
      <!-- 操作按钮 -->
      <div class="share-actions">
        <el-button 
          type="primary" 
          :loading="generating"
          @click="generateAndShare"
        >
          {{ generating ? '生成中...' : '生成图片并分享' }}
        </el-button>
      </div>
    </div>
    
    <div slot="footer" class="dialog-footer">
      <el-button @click="visible = false">关闭</el-button>
    </div>
  </el-dialog>
</template>

<script>
import html2canvas from 'html2canvas'

export default {
  name: 'ReceiptShareDialog',
  data() {
    return {
      visible: false,
      order: null,
      generating: false,
      // 商户信息
      merchantName: 'WHOLESALE SYSTEM',
      merchantAddress: '',
      merchantPhone: '',
      gstNo: '',
      gstRate: 9,
      showGst: false,
      // 银行信息
      showBankInfo: false,
      bankName: '',
      bankAccountName: '',
      bankAccountNumber: ''
    }
  },
  computed: {
    orderTime() {
      if (!this.order?.createdAt) return ''
      const date = new Date(this.order.createdAt)
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const day = date.getDate()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`
    },
    gstAmount() {
      if (this.order?.gstAmount > 0) return this.order.gstAmount
      // 计算商品 GST 总和
      let total = 0
      if (this.order?.items) {
        this.order.items.forEach(item => {
          if (item.taxAmount > 0) total += item.taxAmount
        })
      }
      return total
    }
  },
  methods: {
    show(order, config = {}) {
      this.order = order
      this.merchantName = config.merchantName || order.merchantName || 'WHOLESALE SYSTEM'
      this.merchantAddress = config.merchantAddress || order.merchantAddress || ''
      this.merchantPhone = config.merchantPhone || ''
      this.gstNo = config.gstNo || ''
      this.gstRate = config.gstRate || 9
      this.showGst = config.showGst || false
      this.showBankInfo = config.showBankInfo || false
      this.bankName = config.bankName || ''
      this.bankAccountName = config.bankAccountName || ''
      this.bankAccountNumber = config.bankAccountNumber || ''
      this.visible = true
    },
    
    truncate(str, maxLen) {
      if (!str) return ''
      return str.length <= maxLen ? str : str.substring(0, maxLen)
    },
    
    formatMoney(val) {
      if (val == null) return '0.00'
      return Number(val).toFixed(2).padStart(7)
    },
    
    // 直接生成图片（不弹窗）
    async directGenerate(order, config = {}, receivableData = null) {
      // 准备配置
      const merchantName = config.merchantName || order.merchantName || 'WHOLESALE SYSTEM'
      const merchantAddress = config.merchantAddress || order.merchantAddress || ''
      const merchantPhone = config.merchantPhone || ''
      const gstNo = config.gstNo || ''
      const gstRate = config.gstRate || 9
      const showGst = config.showGst || false
      const showBankInfo = config.showBankInfo || false
      const bankName = config.bankName || ''
      const bankAccountName = config.bankAccountName || ''
      const bankAccountNumber = config.bankAccountNumber || ''

      const orderTime = order.createdAt ? (() => {
        const d = new Date(order.createdAt)
        return `${d.getFullYear()}/${d.getMonth()+1}/${d.getDate()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`
      })() : ''

      let gstAmount = order.gstAmount || 0
      if (!gstAmount && order.items) {
        order.items.forEach(it => { if (it.taxAmount > 0) gstAmount += it.taxAmount })
      }

      const fmt = v => v == null ? '0.00' : Number(v).toFixed(2).padStart(7)
      const tr = (s, n) => !s ? '' : s.length <= n ? s : s.substring(0, n)

      // 构建商品明细 HTML
      let itemsHtml = ''
      ;(order.items || []).forEach((item, idx) => {
        itemsHtml += `<div class="item-row">
          <p class="item-name">${String(idx+1).padStart(2,'0')} ${tr(item.productName||item.name,12)} ${String(item.quantity).padStart(5)} ${String(item.subtotal).padStart(7)}</p>
          ${item.taxRate > 0 ? `<p class="item-detail">GST ${Math.round(item.taxRate*100)}%: ${fmt(item.taxAmount)}</p>` : (item.taxRate === 0 ? '<p class="item-detail">(Tax Free)</p>' : '')}
        </div>`
      })

      // 构建离屏 receipt 容器
      const el = document.createElement('div')
      el.className = 'receipt-content'
      el.style.cssText = 'position:fixed;left:-9999px;top:0;width:300px;background:#fff;z-index:-1;'
      el.innerHTML = `
        <div class="receipt-header">
          <h2>${merchantName}</h2>
          ${merchantAddress ? `<p>${merchantAddress}</p>` : ''}
          ${merchantPhone ? `<p>Tel: ${merchantPhone}</p>` : ''}
          ${gstNo ? `<p>GST No: ${gstNo}</p>` : ''}
        </div>
        <div class="receipt-divider">********************************</div>
        <div class="receipt-info">
          <p>Terminal: ${order.sourceChannel || 'WEB'}</p>
          <p>${orderTime}</p>
          <p>Invoice No: ${order.invoiceNo || order.orderNo || 'N/A'}</p>
          <p>Order: ${order.orderNo}</p>
          <p>Cashier: ${order.createdBy || '001'}</p>
        </div>
        <div class="receipt-divider">--------------------------------</div>
        <div class="receipt-items">
          <p class="item-header">No   Item            Qty    Amt</p>
          <div class="receipt-divider">--------------------------------</div>
          ${itemsHtml}
          <div class="receipt-divider">--------------------------------</div>
        </div>
        <div class="receipt-summary">
          <p>Subtotal:    SGD${fmt(order.subtotal)}</p>
          ${showGst ? `<p>GST (${gstRate}%):   SGD${fmt(gstAmount)}</p>` : (gstAmount > 0 ? `<p>GST:         SGD${fmt(gstAmount)}</p>` : '<p>Tax Free</p>')}
          <div class="receipt-divider">--------------------------------</div>
          <p class="total">Total:       SGD${fmt(order.totalAmount)}</p>
          <p>Items: ${(order.items||[]).length}</p>
          <p>Pay: ${order.paymentMethod || 'COD'}</p>
          ${showBankInfo ? `
            <div class="receipt-divider">--------------------------------</div>
            <p>Bank: ${bankName}</p>
            ${bankAccountName ? `<p>Acc:  ${bankAccountName}</p>` : ''}
            ${bankAccountNumber ? `<p>No:   ${bankAccountNumber}</p>` : ''}
          ` : ''}
          <div class="receipt-divider">--------------------------------</div>
          <p class="barcode">${order.orderNo}</p>
          ${order.remark ? `<div class="receipt-divider">--------------------------------</div><p><b>Note</b></p><p>${tr(order.remark,30)}</p>` : ''}
          <div class="receipt-divider">--------------------------------</div>
          ${receivableData?.totalUnpaid > 0 ? `
            <p style="font-weight:bold;">Outstanding Balance: ${receivableData.customerName || ''}</p>
            <div class="receipt-divider">--------------------------------</div>
            <p style="font-size:10px;">Order No      Date          Unpaid</p>
            <div class="receipt-divider">--------------------------------</div>
            ${(receivableData.orders || []).map(o => `<p style="font-size:10px;">${(o.orderNo||'').substring(0,14).padEnd(14)} ${(o.orderDate||'').padEnd(12)} $${parseFloat(o.unpaidAmount||0).toFixed(2)}</p>`).join('')}
            <div class="receipt-divider">--------------------------------</div>
            <p style="font-weight:bold;">Total Unpaid:  $${parseFloat(receivableData.totalUnpaid||0).toFixed(2)}</p>
            <p>(${receivableData.unpaidOrderCount || 0} orders)</p>
            <div class="receipt-divider">--------------------------------</div>
          ` : ''}
          <p class="footer">Thank You / 谢谢惠顾</p>
          <p class="footer">Exchange Within 3 Days</p>
          <div class="receipt-divider">================================</div>
        </div>
      `
      document.body.appendChild(el)

      try {
        const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', useCORS: true })
        canvas.toBlob(async (blob) => {
          document.body.removeChild(el)
          const file = new File([blob], `receipt_${order.orderNo}.png`, { type: 'image/png' })
          const canShare = navigator.share && navigator.canShare && navigator.canShare({ files: [file] })
          if (canShare) {
            try {
              await navigator.share({ files: [file], title: '订单小票', text: `订单号: ${order.orderNo}` })
            } catch (err) {
              if (err.name !== 'AbortError') {
                // 分享失败，降级下载
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a'); a.href = url; a.download = `receipt_${order.orderNo}.png`
                document.body.appendChild(a); a.click(); document.body.removeChild(a)
                setTimeout(() => URL.revokeObjectURL(url), 1000)
              }
            }
          } else {
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a'); a.href = url; a.download = `receipt_${order.orderNo}.png`
            document.body.appendChild(a); a.click(); document.body.removeChild(a)
            setTimeout(() => URL.revokeObjectURL(url), 1000)
          }
        }, 'image/png', 0.95)
      } catch (err) {
        document.body.removeChild(el)
        console.error('生成图片失败:', err)
        throw err
      }
    },

    async generateAndShare() {
      this.generating = true
      try {
        const element = document.getElementById('receipt-preview')
        const canvas = await html2canvas(element, {
          scale: 2,
          backgroundColor: '#ffffff',
          useCORS: true
        })
        
        // 转换为 Blob
        canvas.toBlob(async (blob) => {
          const file = new File([blob], `receipt_${this.order.orderNo}.png`, {
            type: 'image/png'
          })
          
          // 检查是否支持 Web Share API with files
          const canShareFiles = navigator.share && navigator.canShare && navigator.canShare({ files: [file] })
          
          if (canShareFiles) {
            try {
              await navigator.share({
                files: [file],
                title: '订单小票',
                text: `订单号: ${this.order.orderNo}`
              })
              this.$message.success('分享成功')
              this.visible = false
            } catch (err) {
              if (err.name === 'AbortError') {
                // 用户取消分享，不显示错误
                console.log('用户取消分享')
              } else {
                console.error('分享失败:', err)
                this.$message.error('分享失败: ' + err.message)
              }
            }
          } else {
            // 降级方案：先下载图片，然后提示用户手动分享
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `receipt_${this.order.orderNo}.png`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            
            // 延迟释放 URL
            setTimeout(() => URL.revokeObjectURL(url), 1000)
            
            this.$message.success('图片已下载，请手动分享到需要的地方')
            this.visible = false
          }
        }, 'image/png', 0.95)
      } catch (error) {
        console.error('生成图片失败:', error)
        this.$message.error('生成图片失败: ' + error.message)
      } finally {
        this.generating = false
      }
    }
  }
}
</script>

<style scoped>
.receipt-preview {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
  max-height: 500px;
  overflow-y: auto;
}

.receipt-content {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.4;
  max-width: 300px;
  margin: 0 auto;
  padding: 10px;
  background: white;
}

.receipt-header h2 {
  margin: 0;
  text-align: center;
  font-size: 16px;
  font-weight: bold;
}

.receipt-header p {
  margin: 2px 0;
  text-align: center;
  font-size: 11px;
}

.receipt-divider {
  text-align: center;
  margin: 5px 0;
  white-space: pre;
  overflow: hidden;
}

.receipt-info p,
.receipt-summary p,
.item-name,
.item-detail {
  margin: 2px 0;
}

.item-header {
  font-weight: bold;
  margin: 2px 0;
}

.total {
  font-weight: bold;
}

.barcode {
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 2px;
}

.footer {
  text-align: center;
  margin: 2px 0;
}

.share-actions {
  margin-top: 20px;
  text-align: center;
}

.dialog-footer {
  text-align: right;
}
</style>
